import * as Menu from "@radix-ui/react-dropdown-menu";
import { Link } from "@tanstack/react-router";
import {
  Archive,
  ArchiveRestore,
  ArrowRight,
  Trash2,
  UserPlus,
} from "lucide-react";
import type { AthleteStatus } from "@/lib/types";
import { athleteActionFlags } from "../athleteDisplay";
import "@/features/matches/match-actions.css";
import "../athlete-actions.css";

export type AthleteRowActionHandlers = {
  onEdit: () => void;
  onRoster: () => void;
  onArchive: () => void;
  onRestore: () => void;
  onDelete: () => void;
};

/**
 * Directory row quick actions (#908) — the Match Library row pattern
 * (`features/matches/components/MatchActions.tsx`): an inline Edit button
 * plus a "More" menu (Radix dropdown, keyboard-navigable) holding Open,
 * Roster on a team, Archive / Restore and the guarded Delete. Visibility
 * follows the API's per-row rights (`athleteActionFlags`); below 960px the
 * row becomes a card and the actions sit on its last line.
 */
export function AthleteRowActions({
  athlete,
  testIdPrefix = "admin-athlete-row",
  restoring = false,
  ...handlers
}: AthleteRowActionHandlers & {
  athlete: {
    id: string;
    display_name: string;
    status: AthleteStatus;
    can_edit?: boolean;
    can_archive?: boolean;
  };
  testIdPrefix?: string;
  /** This athlete's restore is in flight: Restore is disabled. */
  restoring?: boolean;
}) {
  const flags = athleteActionFlags(athlete);
  return (
    <div
      className="match-row-actions athlete-row-actions"
      data-athlete-actions={athlete.id}
    >
      {flags.edit ? (
        <button
          type="button"
          data-testid={`${testIdPrefix}-edit`}
          aria-label="Edit"
          onClick={handlers.onEdit}
        >
          Edit
        </button>
      ) : null}
      <Menu.Root modal={false}>
        <Menu.Trigger asChild>
          <button
            type="button"
            data-testid={`${testIdPrefix}-more`}
            aria-label={`More options for ${athlete.display_name}`}
          >
            More
          </button>
        </Menu.Trigger>
        <Menu.Portal>
          <Menu.Content
            className="match-row-menu"
            sideOffset={6}
            align="end"
            collisionPadding={12}
            updatePositionStrategy="always"
            data-testid={`${testIdPrefix}-menu`}
            onClick={(event) => event.stopPropagation()}
          >
            <Menu.Item asChild>
              <Link
                to="/admin/athletes/$athleteId"
                params={{ athleteId: athlete.id }}
                data-testid={`${testIdPrefix}-open`}
              >
                <ArrowRight size={17} aria-hidden />
                Open athlete
              </Link>
            </Menu.Item>
            {flags.roster ? (
              <Menu.Item
                onSelect={handlers.onRoster}
                data-testid={`${testIdPrefix}-roster`}
              >
                <UserPlus size={17} aria-hidden />
                Roster on a team
              </Menu.Item>
            ) : null}
            {flags.archive ? (
              <Menu.Item
                onSelect={handlers.onArchive}
                data-testid={`${testIdPrefix}-archive`}
              >
                <Archive size={17} aria-hidden />
                Archive
              </Menu.Item>
            ) : null}
            {flags.restore ? (
              <Menu.Item
                onSelect={handlers.onRestore}
                disabled={restoring}
                data-testid={`${testIdPrefix}-restore`}
              >
                <ArchiveRestore size={17} aria-hidden />
                Restore
              </Menu.Item>
            ) : null}
            {flags.delete ? (
              <Menu.Item
                onSelect={handlers.onDelete}
                className="match-row-menu-danger"
                data-testid={`${testIdPrefix}-delete`}
              >
                <Trash2 size={17} aria-hidden />
                Delete athlete
              </Menu.Item>
            ) : null}
          </Menu.Content>
        </Menu.Portal>
      </Menu.Root>
    </div>
  );
}
